import React from 'react';
import { useTranslation } from 'react-i18next';
import ReportFooter from './ReportFooter';
import './ReportBilanz.css';

/**
 * Komponente für Bilanz-Reports (Zeitarten und Kürzel)
 */
function ReportBilanz({
  analysis,
  ablaufDbData,
  decimalPlaces,
  formatTime,
  formatSollzeit,
  formatGrade,
  availableTimeTypes,
  type = 'zeitarten', // 'zeitarten' oder 'kuerzel'
  startPage = 2,
  totalPages = 2,
  pageBreakAfter = 'auto',
  userEmail,
  userDeviceName
}) {
  const { t } = useTranslation();
  if (!analysis) return null;

  if (type === 'zeitarten') {
    // Bilanz nach Zeitarten
    // availableTimeTypes ist ein Array von Objekten {name, description, color}
    const timeTypeData = availableTimeTypes.map(timeTypeObj => {
      const timeTypeName = timeTypeObj.name || timeTypeObj; // Fallback falls es doch ein String ist
      const data = analysis.byTimeType[timeTypeName] || { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
      return { 
        timeTypeName, 
        timeTypeDescription: timeTypeObj.description || '', 
        ...data 
      };
    });

    const grandTotal = {
      count: timeTypeData.reduce((sum, d) => sum + d.count, 0),
      totalTime: timeTypeData.reduce((sum, d) => sum + d.totalTime, 0),
      totalVorgabeTime: timeTypeData.reduce((sum, d) => sum + (d.totalVorgabeTime || 0), 0),
      grades: timeTypeData.flatMap(d => d.grades)
    };

    return (
      <div className="report-section report-bilanz-zeitarten" style={{ pageBreakAfter, position: 'relative', paddingBottom: '25mm' }}>
        <ReportFooter 
          pageNumber={startPage} 
          totalPages={totalPages}
          userEmail={userEmail}
          userDeviceName={userDeviceName}
        />
        <h2 style={{ marginTop: '0', marginBottom: '10px', fontSize: '18px', fontWeight: 'bold' }}>{t('report.bilanz.zeitarten.title')}</h2>
        <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginBottom: '20px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'left' }}>{t('report.header.zeitart')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'left' }}>{t('report.header.beschreibung')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>{t('report.header.anzahl')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>{t('report.header.gesamtzeit')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>{t('report.header.vorgabezeit')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>{t('report.header.leistungsgrad')}</th>
            </tr>
          </thead>
          <tbody>
            {timeTypeData.map(({ timeTypeName, timeTypeDescription, count, totalTime, totalVorgabeTime, avgGrade }) => (
              <tr key={timeTypeName}>
                <td style={{ border: '1px solid #ccc', padding: '8px', fontWeight: 'bold' }}>{timeTypeName}</td>
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{timeTypeDescription || '-'}</td>
                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{count}</td>
                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatTime(totalTime)}</td>
                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatSollzeit(totalVorgabeTime || 0, decimalPlaces)} {t('report.unit.min')}</td>
                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatGrade(avgGrade)}</td>
              </tr>
            ))}
            <tr style={{ backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{t('report.gesamt')}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{grandTotal.count}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatTime(grandTotal.totalTime)}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatSollzeit(grandTotal.totalVorgabeTime || 0, decimalPlaces)} {t('report.unit.min')}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  } else {
    // Bilanz nach Kürzel
    if (!analysis.byKuerzel) return null;

    const kuerzels = Object.keys(analysis.byKuerzel || {}).sort((a, b) => {
      if (a === 'Kein Kürzel') return 1;
      if (b === 'Kein Kürzel') return -1;
      return a.localeCompare(b);
    });

    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };

    kuerzels.forEach(kuerzel => {
      const data = analysis.byKuerzel[kuerzel];
      grandTotal.count += data.count;
      grandTotal.totalTime += data.totalTime;
      grandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
      grandTotal.grades.push(...data.grades);
    });

    const getKuerzelDescription = (kuerzelName) => {
      if (kuerzelName === 'Kein Kürzel') return '';
      const kuerzelObj = ablaufDbData?.find(k => k.kuerzel === kuerzelName);
      return kuerzelObj ? (kuerzelObj.beschreibung || kuerzelObj.fullText || '') : '';
    };

    return (
      <div className="report-section report-bilanz-kuerzel">
        <ReportFooter 
          pageNumber={startPage} 
          totalPages={totalPages}
          userEmail={userEmail}
          userDeviceName={userDeviceName}
        />
        <h2 style={{ marginTop: '0', marginBottom: '10px', fontSize: '18px', fontWeight: 'bold' }}>{t('report.bilanz.kuerzel.title')}</h2>
        <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginBottom: '20px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'left' }}>{t('report.header.kuerzel')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'left' }}>{t('report.header.taetigkeit')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>{t('report.header.anzahl')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>{t('report.header.gesamtzeit')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>{t('report.header.vorgabezeit')}</th>
              <th style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>{t('report.header.leistungsgrad')}</th>
            </tr>
          </thead>
          <tbody>
            {kuerzels.map(kuerzel => {
              const data = analysis.byKuerzel[kuerzel];
              const description = getKuerzelDescription(kuerzel);
              const displayKuerzel = kuerzel === 'Kein Kürzel' ? t('report.kein_kuerzel') : kuerzel;
              return (
                <tr key={kuerzel}>
                  <td style={{ border: '1px solid #ccc', padding: '8px', fontWeight: 'bold' }}>{displayKuerzel}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{description || '-'}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{data.count}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatTime(data.totalTime)}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatSollzeit(data.totalVorgabeTime || 0, decimalPlaces)} {t('report.unit.min')}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatGrade(data.avgGrade)}</td>
                </tr>
              );
            })}
            <tr style={{ backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{t('report.gesamt')}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{grandTotal.count}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatTime(grandTotal.totalTime)}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatSollzeit(grandTotal.totalVorgabeTime || 0, decimalPlaces)} {t('report.unit.min')}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

export default ReportBilanz;
